import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EducationProgramDTO } from '@core/apis/education/education.dto';
import { DatepickerInputComponent } from '@core/components/datepicker-input/datepicker-input.component';
import { BookingStorageEnum } from '@core/enums/booking.enum';
import { IBookingBasketItem, IBookingStorageDeleteEvent, IFormatTimeData } from '@core/interfaces/booking.interface';
import { IUserStore } from '@core/interfaces/interface';
import { UserStore } from '@core/stores/user.store';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EducationViewComponent } from '../../education-view/education-view.component';
import { FormatTimeDataTypeEnum } from '@oranta-mis/sdk';

@Component({
  selector: 'app-education-order',
  imports: [EducationViewComponent, ReactiveFormsModule, TranslocoModule, DatepickerInputComponent],
  standalone: true,
  templateUrl: './education-order.component.html',
  styleUrl: './education-order.component.scss',
})
@UntilDestroy()
export class EducationOrderComponent {
  public program!: EducationProgramDTO;
  public form!: FormGroup;
  public user: IUserStore | null = null;
  public durationType!: FormatTimeDataTypeEnum;
  public readonly FormatTimeDataTypeEnum = FormatTimeDataTypeEnum;
  public hideDate: boolean = false;
  public hideNote: boolean = false;

  @Input() set programValue(p: EducationProgramDTO) {
    if (p) {
      this.program = p;
    }
  }

  @Output() delete: EventEmitter<IBookingStorageDeleteEvent> = new EventEmitter<IBookingStorageDeleteEvent>();
  @Output() formValue: EventEmitter<IBookingBasketItem> = new EventEmitter<IBookingBasketItem>();

  constructor(
    private readonly userStore: UserStore,
    private readonly fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.user = this.userStore.get();
    this.reactiveForm();
    this.formValue.emit({
      type: BookingStorageEnum.education,
      form: this.form,
    });
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((v) => {
      this.formValue.emit({
        type: BookingStorageEnum.education,
        form: this.form,
      });
    });

    const user = this.userStore.get();
    if (user) this.form.get('patientId')?.setValue(user.id);
    this.form.get('id')?.setValue(this.program.id);
    let value: IFormatTimeData = this.program.formatTime;
    if (value) {
      this.durationType = value.type;
      if (this.durationType == FormatTimeDataTypeEnum.by_date) this.startDate?.setValue(value.startDate);
    }
  }

  private reactiveForm() {
    this.form = this.form = this.fb.group({
      id: [null, Validators.required],
      startDate: [null],
      note: [null],
      patientId: [null, Validators.required],
    });
    this.getHiddenDateAndNoten();
  }

  private getHiddenDateAndNoten(): void {
    const control = this.form.get('startDate');
    if (this.program) {
      if (this.program.formatTime.type == FormatTimeDataTypeEnum.by_date && !this.program.withoutDurationNotes) {
        this.hideDate = true;
        control?.clearValidators();
      } else if (
        (this.program.formatTime.type == FormatTimeDataTypeEnum.by_date ||
          this.program.formatTime.type == FormatTimeDataTypeEnum.by_duration) &&
        this.program.withoutDurationNotes
      ) {
        this.hideDate = true;
        this.hideNote = true;
        control?.clearValidators();
      } else if (
        this.program.formatTime.type == FormatTimeDataTypeEnum.by_duration &&
        !this.program.withoutDurationNotes
      ) {
        control?.setValidators(Validators.required);
      }
    }
  }

  public get startDate(): FormControl {
    return this.form.get('startDate') as FormControl;
  }
}
